// shapes.ts — the generic module surfaces (/verify, /metrics, /raw) as the SERVER sends them.
//
// Written against each handler's own JSON (first 2026-10-09 on branch phase-d-console, re-read on
// main 2026-10-25), because the screen that rendered them had been written against a shape only one
// module sends: it read `checks[]`/`verdict` from every /verify, and only redis answers that way —
// caddy, dns and mail send {consistent, items[], orphans}, mariadb and postgresql name their drift
// lists, glusterfs sends four booleans and sogo {ok, problem, warning}. Seven of eight verify screens
// therefore said "Nothing measured" with no verdict while the server had answered in full — the
// classic silent failure, a correct answer rendered as an empty one. /metrics had the same fault (all
// four modules nest their values under `metrics`, the screen showed only top-level scalars) and
// nftables' /raw sends {content}, not {files}.
//
// The handlers, one per shape (each payload in shapes.test.ts is copied from the one named there):
//   /verify   redis       internal/modules/redis/topology.go       handleVerify
//             caddy       internal/modules/caddy/vhosts.go         handleVerify
//             dns         internal/modules/dns/zones.go            handleVerify
//             mail        internal/modules/mail/mail.go            handleVerify
//             mariadb     internal/modules/mariadb/admin.go        handleVerify
//             postgresql  internal/modules/postgresql/admin.go     handleVerify
//             glusterfs   internal/modules/glusterfs/glusterfs.go  handleVerify
//             sogo        internal/modules/sogo/sogo.go            handleVerify
//   /metrics  mariadb     internal/modules/mariadb/mariadb.go      handleMetrics
//             postgresql  internal/modules/postgresql/postgresql.go handleMetrics
//             redis       internal/modules/redis/redis.go          handleMetrics
//             caddy       internal/modules/caddy/caddy.go          handleMetrics
//   /raw      nftables    internal/modules/nftables/manage.go      handleRaw ({content}); every other
//             module's handleRaw sends {files:[{path,content}]}
//
// Pure functions, so the mapping is tested against the real shapes (shapes.test.ts) rather than
// re-discovered on a live node.

export type VerifyState = "ok" | "warn" | "fail" | "unknown";

export interface VerifyItem {
  key: string;
  label: string;
  state: VerifyState;
  detail?: string;
}

export interface VerifyView {
  /** The module's own verdict; "unknown" when it gave none or said it could not measure. */
  verdict: VerifyState | "none";
  verdictLabel: string;
  items: VerifyItem[];
  /** The server's prose: detail/note (neutral), problem (fail), warning (warn). */
  notes: { tone: "neutral" | "warn" | "fail"; text: string }[];
}

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => v !== null && typeof v === "object" && !Array.isArray(v);
const human = (k: string) => k.replace(/_/g, " ");

// Lists whose ENTRIES are faults. Named, not guessed: sogo also sends `configured`, `expected` and
// `hosted_mail_domains`, which are inventories, and treating every string list as a fault would turn
// a correct configuration red.
const FAULT_LISTS: Record<string, VerifyState> = {
  orphans: "fail", // mail, dns: records with no registered domain
  orphan_files: "fail", // caddy: site files no record produces
  orphan_databases: "fail", // mariadb, postgresql
  missing_databases: "fail",
  missing_users: "fail",
  roles_not_in_pgbouncer: "fail",
  stale_pgbouncer_entries: "fail",
  missing_from_config: "fail", // sogo
  configured_but_not_expected: "fail",
  configured_without_mail: "warn",
};

// Keys that ARE the verdict or its qualifiers, rendered as such rather than as rows.
const VERDICT_KEYS = new Set(["verdict", "grade", "consistent", "ok", "installed", "verified", "summary",
  "detail", "note", "problem", "warning", "checks", "items", "unread", "records", "count",
  // redis: the evidence its checks[] were built from — every failing hop and Sentinel verdict is
  // already a check, so the raw lists would only repeat them as prose.
  "auth_chain", "sentinel_masters"]);

function stateOfStatus(s: unknown): VerifyState {
  switch (String(s ?? "").toLowerCase()) {
    case "pass":
    case "ok":
    case "healthy":
      return "ok";
    case "warn":
    case "warning":
    case "advisory":
      return "warn";
    case "fail":
    case "critical":
    case "error":
      return "fail";
    default:
      return "unknown"; // skip, missing, anything unrecognised: not measured is never a pass
  }
}

function describe(row: Obj): string {
  return Object.entries(row)
    .filter(([, v]) => v === null || typeof v !== "object")
    .map(([k, v]) => `${human(k)}: ${v === true ? "yes" : v === false ? "no" : v ?? "unknown"}`)
    .join(" · ");
}

/** normaliseVerify maps any module's /verify answer onto one view. Unknown never reads as a pass. */
export function normaliseVerify(d: Obj | null | undefined): VerifyView {
  const v: VerifyView = { verdict: "none", verdictLabel: "", items: [], notes: [] };
  if (!d) return v;

  // ── verdict ───────────────────────────────────────────────────────────────
  if (typeof d.verdict === "string" || typeof d.grade === "string") {
    const raw = String(d.verdict ?? d.grade);
    v.verdict = stateOfStatus(raw);
    v.verdictLabel = raw;
  } else if ("consistent" in d) {
    // postgresql sends null when a side could not be read — not measured, never consistent.
    v.verdict = d.consistent === true ? "ok" : d.consistent === false ? "fail" : "unknown";
    v.verdictLabel = d.consistent === true ? "consistent" : d.consistent === false ? "drift" : "not measured";
  } else if ("ok" in d) {
    if (d.installed === false) {
      v.verdict = "none";
      v.verdictLabel = "not installed";
    } else if (d.verified === false) {
      v.verdict = "unknown";
      v.verdictLabel = "not verified";
    } else {
      v.verdict = d.ok === true ? "ok" : d.ok === false ? "fail" : "unknown";
      v.verdictLabel = d.ok === true ? "ok" : d.ok === false ? "problem" : "not measured";
    }
  }

  // ── redis: checks[] ───────────────────────────────────────────────────────
  if (Array.isArray(d.checks)) {
    d.checks.forEach((c, i) => {
      if (!isObj(c)) return;
      const name = String(c.check ?? c.name ?? `check ${i + 1}`);
      let detail = typeof c.detail === "string" ? c.detail : undefined;
      if (isObj(c.settings)) {
        const kv = Object.entries(c.settings).map(([k, val]) => `${k}=${val}`).join(" ");
        detail = detail ? `${detail} (${kv})` : kv;
      }
      v.items.push({ key: `check:${name}:${i}`, label: human(name), detail,
        state: c.ok === true ? "ok" : c.ok === false ? "fail" : stateOfStatus(c.status) });
    });
  }

  // ── caddy / dns / mail: items[] each with ok ──────────────────────────────
  if (Array.isArray(d.items)) {
    d.items.forEach((it, i) => {
      if (!isObj(it)) return;
      const name = String(it.domain ?? it.name ?? it.database ?? `item ${i + 1}`);
      const bad = Object.entries(it).filter(([k, val]) => k !== "ok" && val === false).map(([k]) => human(k));
      v.items.push({ key: `item:${name}:${i}`, label: name,
        state: it.ok === true ? "ok" : it.ok === false ? "fail" : "unknown",
        detail: bad.length ? bad.map((k) => `${k}: no`).join(" · ") : undefined });
    });
  }

  // ── postgresql: what could not be read ────────────────────────────────────
  if (isObj(d.unread)) {
    for (const [k, why] of Object.entries(d.unread)) {
      v.items.push({ key: `unread:${k}`, label: human(k), state: "unknown", detail: `could not be read: ${String(why)}` });
    }
  }

  // ── everything else, by its own type ─────────────────────────────────────
  const unread = isObj(d.unread) ? d.unread : {};
  for (const [k, val] of Object.entries(d)) {
    if (VERDICT_KEYS.has(k) || k in unread) continue;
    if (Array.isArray(val)) {
      const entries = val.map((e) => (isObj(e) ? describe(e) : String(e)));
      if (k in FAULT_LISTS) {
        v.items.push({ key: `list:${k}`, label: human(k),
          state: entries.length ? FAULT_LISTS[k]! : "ok",
          detail: entries.length ? entries.join(", ") : "none" });
      } else {
        // An inventory — what the check compared — shown, never judged.
        v.notes.push({ tone: "neutral", text: `${human(k)}: ${entries.length ? entries.join(", ") : "none"}` });
      }
    } else if (typeof val === "boolean") {
      // glusterfs: mount_present, mount_writable, volume_up
      v.items.push({ key: `bool:${k}`, label: human(k), state: val ? "ok" : "fail" });
    } else if (typeof val === "string" && k.endsWith("_drift")) {
      // postgresql repmgr_drift: empty is agreement, text is the disagreement
      v.items.push({ key: `drift:${k}`, label: human(k), state: val ? "fail" : "ok", detail: val || "agrees" });
    } else if (isObj(val) && ("verdict" in val || "status" in val)) {
      // postgresql pgbouncer: the pooler's own verdict, folded in
      v.items.push({ key: `sub:${k}`, label: human(k), state: stateOfStatus(val.verdict ?? val.status), detail: describe(val) });
    } else if (isObj(val) && Object.keys(val).length === 0) {
      // an object the server could not fill (pgbouncer when its chain was unread — named in unread)
      continue;
    } else if (val !== null && typeof val !== "object") {
      v.notes.push({ tone: "neutral", text: `${human(k)}: ${String(val)}` });
    }
  }

  // ── the server's own prose ───────────────────────────────────────────────
  if (typeof d.records === "number") v.notes.unshift({ tone: "neutral", text: `${d.records} record(s) compared` });
  if (typeof d.problem === "string" && d.problem) v.notes.unshift({ tone: "fail", text: d.problem });
  if (typeof d.warning === "string" && d.warning) v.notes.push({ tone: "warn", text: d.warning });
  for (const k of ["summary", "detail", "note"]) {
    const t = d[k];
    if (typeof t === "string" && t) v.notes.push({ tone: "neutral", text: t });
  }
  return v;
}

export interface MetricRow {
  key: string;
  value: unknown;
  /** Set when the value could not be read — rendered as that, never as a blank or a zero. */
  unread?: string;
}

/** flattenMetrics: every module that serves /metrics nests its values under `metrics`; mariadb adds
 *  top-level scalars and an `unread` map naming what it could not read. */
export function flattenMetrics(d: Obj | null | undefined): MetricRow[] {
  if (!d) return [];
  const rows: MetricRow[] = [];
  const unread = isObj(d.unread) ? d.unread : {};
  const push = (k: string, val: unknown) => {
    if (k in unread) rows.push({ key: k, value: null, unread: String(unread[k]) });
    // An empty string is not a reading: postgresql's handler sends "" for a query that failed. Shown as
    // what it is, never as a blank that reads like a value.
    else if (val === "") rows.push({ key: k, value: null, unread: "no value was returned" });
    else if (val === null || typeof val !== "object") rows.push({ key: k, value: val });
    else rows.push({ key: k, value: JSON.stringify(val) });
  };
  for (const [k, val] of Object.entries(d)) {
    if (k === "unread" || k === "note") continue;
    if (k === "metrics" && isObj(val)) {
      for (const [mk, mv] of Object.entries(val).sort(([a], [b]) => a.localeCompare(b))) push(mk, mv);
    } else {
      push(k, val);
    }
  }
  return rows;
}

export interface RawFile {
  path: string;
  content?: string;
}

/** rawFiles: most modules answer /raw with {files:[{path,content}]}; nftables answers {content}. */
export function rawFiles(d: Obj | null | undefined, module: string): RawFile[] {
  if (!d) return [];
  if (Array.isArray(d.files)) {
    return d.files.filter(isObj).map((f) => ({ path: String(f.path ?? ""), content: typeof f.content === "string" ? f.content : undefined }));
  }
  if (typeof d.content === "string") return [{ path: `${module} (as running)`, content: d.content }];
  return [];
}
